import React, { useState, useEffect, useRef } from 'react';
import { useTranslations } from '../../contexts/TranslationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrencyValue } from '../../utils/formatCurrency';
import { getGoalTheme } from '../../config/goalThemes';
import { useDraft } from '../../contexts/DraftContext';
import api, { apiRoutes } from '../../api';
import { getConfig } from '../../config/environment';
import GoalThermometerAdminPanel from '../GoalThermometerAdminPanel';

const clampPct = (pct) => Math.max(0, Math.min(100, Number(pct) || 0));

// --- Shape renderers -------------------------------------------------------

function Thermometer({ theme, pct, raised, goal, title, raisedLabel, ofGoalLabel, goalLabel, fmt }) {
  const fillHeight = `${clampPct(pct)}%`;
  return (
    <div className="flex flex-col items-center gap-3">
      {title ? <h3 className="text-lg font-bold text-gray-900 dark:text-white" style={{ color: theme.labelColor }}>{title}</h3> : null}
      <div className="relative flex items-end" style={{ height: 220 }}>
        {/* Tube */}
        <div className="relative w-8 rounded-full overflow-hidden" style={{ height: 200, backgroundColor: theme.track }}>
          <div className="absolute bottom-0 left-0 w-full transition-all duration-700" style={{ height: fillHeight, backgroundColor: theme.fill }} />
        </div>
        {/* Bulb */}
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full" style={{ width: 44, height: 44, backgroundColor: theme.fill }} />
      </div>
      <div className="text-center">
        <div className="text-2xl font-extrabold text-gray-900 dark:text-white">{fmt(raised)}</div>
        <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-white/60">{raisedLabel}</div>
        <div className="text-sm font-semibold mt-2 text-gray-500 dark:text-white/60">{goalLabel}: {fmt(goal)}</div>
        <div className="text-sm font-bold mt-1" style={{ color: theme.accent }}>{clampPct(pct).toFixed(0)}% {ofGoalLabel} {goalLabel}</div>
      </div>
    </div>
  );
}

function Bar({ theme, pct, raised, goal, title, raisedLabel, ofGoalLabel, goalLabel, fmt }) {
  return (
    <div className="w-full max-w-xl mx-auto flex flex-col gap-2 px-1 sm:px-2">
      {title ? <h3 className="text-base font-bold text-center mb-1 text-gray-900 dark:text-white">{title}</h3> : null}
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-lg sm:text-xl font-extrabold whitespace-nowrap text-gray-900 dark:text-white">
          {fmt(raised)} <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-white/60">{raisedLabel}</span>
        </span>
        <span className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-white/60 whitespace-nowrap">{goalLabel}: {fmt(goal)}</span>
      </div>
      <div className="relative w-full rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700" style={{ height: 20 }}>
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
          style={{
            width: `${clampPct(pct)}%`,
            background: theme.fill,
          }}
        />
      </div>
      <div className="text-center text-sm font-bold" style={{ color: theme.accent }}>{clampPct(pct).toFixed(0)}% {ofGoalLabel} {goalLabel}</div>
    </div>
  );
}

function Radial({ theme, pct, raised, goal, title, raisedLabel, ofGoalLabel, goalLabel, fmt }) {
  const size = 160;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - clampPct(pct) / 100);
  return (
    <div className="flex flex-col items-center gap-2">
      {title ? <h3 className="text-lg font-bold text-gray-900 dark:text-white" style={{ color: theme.labelColor }}>{title}</h3> : null}
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={theme.track} strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" stroke={theme.fill} strokeWidth={stroke}
            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.7s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-extrabold text-gray-900 dark:text-white">{clampPct(pct).toFixed(0)}%</span>
        </div>
      </div>
      <div className="text-center">
        <div className="text-lg font-bold text-gray-900 dark:text-white">{fmt(raised)} <span className="text-xs text-gray-500 dark:text-white/60">{raisedLabel}</span></div>
        <div className="text-sm text-gray-500 dark:text-white/60 mt-1">{goalLabel}: {fmt(goal)}</div>
      </div>
    </div>
  );
}

function Badge({ theme, pct, raised, goal, title, raisedLabel, ofGoalLabel, goalLabel, fmt }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="rounded-full flex flex-col items-center justify-center text-white shadow-lg"
        style={{ width: 170, height: 170, background: theme.fill }}
      >
        <span className="text-2xl font-extrabold">{fmt(raised)}</span>
        <span className="text-[11px] uppercase tracking-wider opacity-90">{raisedLabel}</span>
      </div>
      <div className="text-center">
        {title ? <div className="text-sm font-bold" style={{ color: theme.labelColor }}>{title}</div> : null}
        <div className="text-sm opacity-70">{goalLabel}: {fmt(goal)}</div>
        <div className="text-xs font-bold mt-1" style={{ color: theme.accent }}>{clampPct(pct).toFixed(0)}% {ofGoalLabel} {goalLabel}</div>
      </div>
    </div>
  );
}

const SHAPES = { thermometer: Thermometer, bar: Bar, radial: Radial, badge: Badge };

/**
 * Fundraising goal section — renders a themed progress meter driven by the
 * OrganizationGoal fields on the resolved next-draw payload.
 *
 * Props:
 *   mainDraw – resolved next-draw (goal_* fields)
 *   isAdmin  – show the admin settings overlay
 *   editMode – admin edit mode flag
 *   onGoalSaved – refetch callback after admin saves
 */
export default function GoalThermometerSection({ mainDraw, isAdmin, editMode }) {
  const { t } = useTranslations();
  const { language } = useLanguage();
  const draft = useDraft();
  const showAdmin = !!(isAdmin || editMode);
  const [localOverrides, setLocalOverrides] = useState(null);
  const [liveRaised, setLiveRaised] = useState(null);
  const intervalRef = useRef(null);

  // Resolve refresh interval from draft or mainDraw
  const refreshMs = localOverrides?.refreshMs
    ?? draft.getDraftAppearance?.('goalRefreshMs')
    ?? mainDraw?.goal_refresh_ms
    ?? 30000;

  // Poll for updated raised amount
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const isEnabled = localOverrides?.enabled ?? draft.getDraftAppearance?.('goalEnabled') ?? mainDraw?.goal_enabled;
    if (!isEnabled || editMode) return;

    const fetchRaised = async () => {
      try {
        const { tlfId } = getConfig();
        const res = await api.get(apiRoutes.OrganizationNextDraw, { params: { organizationId: tlfId } });
        const draw = res.data?.payload || res.data;
        if (draw?.goal_amount_raised != null) {
          setLiveRaised(draw.goal_amount_raised);
        }
      } catch { /* non-critical */ }
    };

    const ms = Math.max(5000, refreshMs);
    intervalRef.current = setInterval(fetchRaised, ms);
    return () => clearInterval(intervalRef.current);
  }, [refreshMs, localOverrides?.enabled, mainDraw?.goal_enabled, editMode]);

  // Hide entirely for the public when no goal is configured; admins still see
  // the settings overlay so they can enable/configure it.
  const goalEnabled = localOverrides?.enabled ?? draft.getDraftAppearance?.('goalEnabled') ?? mainDraw?.goal_enabled;
  if (!goalEnabled && !showAdmin) return null;

  const activeTheme = localOverrides?.theme ?? draft.getDraftAppearance?.('goalTheme') ?? mainDraw?.goal_theme;
  const activeTitle = localOverrides?.title ?? draft.getDraftAppearance?.('goalTitle') ?? mainDraw?.goal_title ?? '';
  const activeAmount = localOverrides?.amount ?? draft.getDraftAppearance?.('goalAmount') ?? mainDraw?.goal_amount;

  const themeConfig = getGoalTheme(activeTheme);
  const ShapeComponent = SHAPES[themeConfig.shape] || Thermometer;

  const raisedLabel = t('goal.raised', 'Raised');
  const ofGoalLabel = t('goal.ofGoal', 'of');
  const goalLabel = t('goal.goalLabel', 'Goal');

  const handleSettingsChanged = (newSettings) => {
    setLocalOverrides(newSettings);
  };

  return (
    <div className="relative overflow-visible rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] dark:backdrop-blur-xl p-4 sm:p-6 shadow-lg dark:shadow-none">
      {/* Settings dropdown — same pattern as RecentSupporters/Leaderboard */}
      {showAdmin && (
        <GoalThermometerAdminPanel mainDraw={mainDraw} onSettingsChanged={handleSettingsChanged} />
      )}

      {goalEnabled ? (
        <div
          className={`flex items-center justify-center p-4 sm:p-6 rounded-xl bg-gray-50/80 dark:bg-white/5 text-gray-900 dark:text-gray-100${showAdmin ? ' mt-4' : ''}`}
        >
          {(() => {
            const rawRaised = liveRaised ?? mainDraw?.goal_amount_raised ?? 0;
            const raised = Math.floor(rawRaised / 2);
            const goal = activeAmount || 1;
            const pct = Math.min(100, Math.round((raised / goal) * 100));
            return (
              <ShapeComponent
                theme={themeConfig}
                pct={pct}
                raised={raised}
                goal={activeAmount}
                title={activeTitle}
                raisedLabel={raisedLabel}
                ofGoalLabel={ofGoalLabel}
                goalLabel={goalLabel}
                fmt={(v) => formatCurrencyValue(v, { language })}
              />
            );
          })()}
        </div>
      ) : (
        showAdmin && (
          <div className={`text-center text-sm text-white/60 py-4${showAdmin ? ' mt-4' : ''}`}>
            {t('goal.notConfigured', 'Fundraising goal is off. Enable it in Goal Settings above.')}
          </div>
        )
      )}
    </div>
  );
}
